import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, ShieldAlert, Mail, Phone, MapPin, Loader2, Check,
} from "lucide-react";
import { toast } from "sonner";
import Brand from "@/components/Brand";

// Public grievance-officer page + complaint form. Required by India IT
// Rules 2021 §3(2). Reachable without auth.

const CATEGORIES = [
  { id: "privacy",  label: "Data privacy" },
  { id: "content",  label: "Objectionable content" },
  { id: "payment",  label: "Payment / billing" },
  { id: "security", label: "Account / security" },
  { id: "other",    label: "Other" },
];

export default function Grievance() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [officer, setOfficer] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("privacy");
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/grievance/officer").then(({ data }) => setOfficer(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3 || message.trim().length < 10) {
      toast.error("Please provide a clear subject (3+ chars) and message (10+ chars).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/grievance", {
        subject: subject.trim(),
        message: message.trim(),
        category,
        name: user ? undefined : name.trim() || undefined,
        email: user ? undefined : email.trim() || undefined,
      });
      setDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit grievance.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div
        className="min-h-[100dvh] flex flex-col items-center justify-center px-6 text-center bg-background"
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="grievance-done"
      >
        <div className="h-14 w-14 border border-accent rounded-full flex items-center justify-center text-accent">
          <Check size={28} strokeWidth={1.5} />
        </div>
        <h1 className="editorial-h2 mt-6">Grievance received.</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-sm">
          Reference: <span className="font-mono">{done.grievance_id}</span>
        </p>
        <p className="text-xs text-muted-foreground mt-4 max-w-sm">
          Per IT Rules 2021, our Grievance Officer will acknowledge within
          <b> {done.officer?.ack_hours || 24} hours</b> and resolve within
          <b> {done.officer?.resolve_days || 15} days</b>.
        </p>
        <Button
          onClick={() => navigate("/")}
          className="mt-8 rounded-none h-11 px-8 uppercase tracking-[0.2em] text-xs"
          data-testid="grievance-done-back-btn"
        >
          Back to app
        </Button>
      </div>
    );
  }

  return (
    <div
      className="min-h-[100dvh] bg-background text-foreground"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="grievance-page"
    >
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em]" data-testid="grievance-back-btn">
            <ArrowLeft size={14} /> Back
          </button>
          <Brand size="sm" />
          <span className="w-16" />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-10 lg:py-16">
        <p className="eyebrow flex items-center gap-2"><ShieldAlert size={12} /> Grievance officer</p>
        <h1 className="editorial-h1 mt-3">Raise a concern.</h1>
        <p className="text-sm text-muted-foreground mt-3 max-w-md">
          Complaints related to privacy, objectionable content, payments, or
          security. Filed under IT Rules 2021.
        </p>

        {officer && (
          <div className="mt-8 border border-border p-5 space-y-2" data-testid="grievance-officer-card">
            <p className="eyebrow">Assigned officer</p>
            <p className="font-serif text-lg">{officer.name}</p>
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Mail size={13} /> <a href={`mailto:${officer.email}`} className="underline underline-offset-4">{officer.email}</a>
            </p>
            {officer.phone && !/^\+91-XXX/.test(officer.phone) && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Phone size={13} /> {officer.phone}
              </p>
            )}
            {officer.address && (
              <p className="text-sm text-muted-foreground flex items-start gap-2">
                <MapPin size={13} className="mt-1 shrink-0" /> <span>{officer.address}</span>
              </p>
            )}
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground pt-2">
              Ack SLA · {officer.ack_hours}h · Resolve SLA · {officer.resolve_days}d
            </p>
          </div>
        )}

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="grievance-form">
          <div>
            <Label className="eyebrow">Category</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {CATEGORIES.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  data-testid={`grievance-cat-${c.id}`}
                  className={`px-3 py-2 text-[10px] uppercase tracking-[0.18em] border ${
                    category === c.id ? "border-accent bg-accent text-accent-foreground" : "border-border hover:border-foreground"
                  }`}
                >{c.label}</button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="gr-subject" className="eyebrow">Subject</Label>
            <Input
              id="gr-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              className="rounded-none mt-2 h-11"
              data-testid="grievance-subject-input"
              required
            />
          </div>

          <div>
            <Label htmlFor="gr-msg" className="eyebrow">Detailed message</Label>
            <Textarea
              id="gr-msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              maxLength={5000}
              className="rounded-none mt-2"
              data-testid="grievance-message-input"
              placeholder="Describe what happened, when, and any evidence. Attach screenshots via email if needed."
              required
            />
            <p className="text-[10px] text-muted-foreground mt-1.5">{message.length} / 5000</p>
          </div>

          {!user && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="gr-name" className="eyebrow">Name (optional)</Label>
                <Input id="gr-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-none mt-2 h-11" data-testid="grievance-name-input" />
              </div>
              <div>
                <Label htmlFor="gr-email" className="eyebrow">Email (optional)</Label>
                <Input id="gr-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none mt-2 h-11" data-testid="grievance-email-input" />
              </div>
            </div>
          )}

          {user && (
            <p className="text-xs text-muted-foreground border border-border p-3">
              Filing as <b>{user.name || user.email}</b> ({user.email}).
            </p>
          )}

          <Button
            type="submit"
            disabled={busy || subject.trim().length < 3 || message.trim().length < 10}
            data-testid="grievance-submit-btn"
            className="w-full h-12 rounded-none uppercase tracking-[0.22em] text-xs"
          >
            {busy ? <><Loader2 size={14} className="animate-spin mr-2" /> Filing</> : "File grievance"}
          </Button>

          <p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em] text-center">
            By filing, you agree to our{" "}
            <Link to="/privacy" className="underline">privacy policy</Link>.
          </p>
        </form>
      </div>
    </div>
  );
}
